import { Client, gql } from 'urql';

import type { GraphqlLanguage } from 'shared/helpers/language';
import {
  createOpportunityCreationPayload,
  OpportunityCreationPayload,
} from 'shared/services/opportunityCreationPayload';

export const CREATE_OPPORTUNITY = gql`
  mutation($attributes: OpportunityAttributes!) {
    createOpportunity(attributes: $attributes) {
      success
      opportunity {
        id
        userId
      }
    }
  }
`;

export async function createOpportunity(
  payload: OpportunityCreationPayload,
  { client }: { client: Client }
) {
  const attributes = createOpportunityCreationPayload(payload);
  const { data: result } = await client
    .mutation(CREATE_OPPORTUNITY, { attributes })
    .toPromise();

  return result;
}

export const AUTHENTICATE_USER = gql`
  mutation AuthenticateUser($email: String!, $password: String!) {
    authenticateUser(credentials: { email: $email, password: $password }) {
      authToken
      userAccount {
        id
        email
        firstName
        lastName
        roles
        owner {
          ... on Customer {
            id
            companyName
            deliveryAddressStreetAndNumber
            deliveryAddressPostalCode
            deliveryAddressCity
            deliveryAddressCountry
            salesforceCustomer {
              billingAddress {
                street
                postalCode
                city
                country
              }
              phoneNumber
            }
          }
        }
      }
    }
  }
`;

export const authenticateUser = (
  email: string,
  password: string,
  { client }: { client: Client }
) => client.mutation(AUTHENTICATE_USER, { email, password }).toPromise();

export const FUNNEL_CHECKOUT = gql`
  mutation funnelCheckout($attributes: FunnelCheckoutAttributes!) {
    funnelCheckout(attributes: $attributes) {
      opportunity {
        id
        userId
      }
      customerAccount {
        uuid
      }
      success
      signInToken
    }
  }
`;

export const CREATE_QF_LEAD = gql`
  mutation createQfLead($attributes: QfLeadAttributes!) {
    createQfLead(attributes: $attributes) {
      success
    }
  }
`;

export const createQfLeadMutation = (
  attributes: QfLeadAttributes,
  { client }: { client: Client }
) => {
  return client.mutation(CREATE_QF_LEAD, { attributes }).toPromise();
};

export interface QfLeadAttributes {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  customerComment: string;
  address: string;
  occasions: [string];
  totalBudgetCents: number;
  numberOfPeople: number;
  numberOfVegans: number;
  numberOfVegetarians: number;
  numberOfOmnivores: number;
  eventDate: string;
  country?: string;
  language?: GraphqlLanguage;
}

export type AccountAttributes = {
  billingAddressCity: string;
  billingAddressPostalCode: string;
  billingAddressStreetAndNumber: string;
  billingEmail?: string;
  billingName: string;
  hasRecurringNeed: boolean;
  name: string;
};

export interface FunnelCheckoutAttributes {
  accountAttributes: AccountAttributes;
  catererSlug?: string;
  opportunityAttributes: ReturnType<typeof createOpportunityCreationPayload>;
  userAttributes: unknown;
}

export const funnelCheckoutMutation = (
  attributes: FunnelCheckoutAttributes,
  { client }: { client: Client }
) => {
  return client.mutation(FUNNEL_CHECKOUT, { attributes }).toPromise();
};
